/**
 * HR-portal FTUE — dashboard extras (`/dashboard`).
 *
 * Owns the one dashboard coachmark that isn't layout-owned: the "Recent
 * Imports" table only renders once `stats.recent_imports.length > 0`, and
 * `stats` is fetched locally in `PortalDashboardPage` -- `use-portal-hr-ftue`
 * (the layout hook) has no access to it, so this step needs its own tiny hook
 * rather than living alongside welcome/dashboard-stats/dashboard-import-cta.
 *
 * A genuinely first-time org has zero imports yet, so this step is naturally
 * skipped on that first visit -- no extra plumbing needed. It becomes a
 * candidate on a later `/dashboard` visit once an import exists, because
 * `use-portal-hr-ftue`'s route-change effect already re-fires `startFlow` on
 * every `/dashboard` visit.
 *
 * Part of the shared `portal-hr-activation` flow. See `flow.ts`.
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

const DASHBOARD_PATH = '/dashboard'

export interface PortalDashboardExtrasFtueArgs {
  /** `stats.recent_imports.length > 0` -- the table is rendered only then. */
  hasRecentImports: boolean
}

export function usePortalDashboardExtrasFtue({ hasRecentImports }: PortalDashboardExtrasFtueArgs) {
  const recentImportsAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.dashboardRecentImports)

  const hasRecentImportsRef = useRef(hasRecentImports)
  hasRecentImportsRef.current = hasRecentImports

  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.dashboardRecentImports,
        phase: PORTAL_HR_STEP.dashboardRecentImports,
        order: PORTAL_HR_ORDER.dashboardRecentImports,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(DASHBOARD_PATH) && hasRecentImportsRef.current,
        anchorId: PORTAL_HR_ANCHOR.dashboardRecentImports,
        placement: 'top',
        hideSkip: true,
        hideDismiss: true,
        autoCompleteMs: 5200,
        content: {
          body: 'Every import you run shows up here, with a quick success/failure count.',
        },
      }),
    ])

    reevaluatePortalHrFlow()
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: hasRecentImports intentionally re-triggers evaluation; live state is read via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId === PORTAL_HR_STEP.dashboardRecentImports) {
      const step = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (step?.condition && !step.condition() && !seen.has(activeStepId)) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [hasRecentImports])

  return { recentImportsAnchor }
}
